import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import axiosInstance, { BASE_URL } from "../../../utils/config/config";
import { compressImage } from "../../../utils/compressImage";
import {
  MULTILINE_ARRAY_FIELDS,
  SINGLE_DOC_FIELDS,
  MULTI_DOC_FIELDS,
  isValidUrl,
} from "../MyProfile/MyProfile.constants";

// Admin-side twin of useMyProfile: same form shape/validation/upload flow, but
// loads a practitioner by id (GET /:id/full) and saves via PUT /:id.

const arrToText = (v) => (Array.isArray(v) ? v.join("\n") : v || "");
const textToArr = (v) =>
  String(v || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

const seedDocs = () => {
  const d = {};
  SINGLE_DOC_FIELDS.forEach((k) => (d[k] = ""));
  MULTI_DOC_FIELDS.forEach((k) => (d[k] = []));
  return d;
};

export const usePractitionerDetail = (practitionerId, open, notify) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [form, setForm] = useState(null);

  const setField = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`practitioners/${practitionerId}/full`);
      const next = { ...seedDocs(), ...data };
      MULTILINE_ARRAY_FIELDS.forEach((f) => (next[f] = arrToText(data[f])));
      setForm(next);
    } catch (err) {
      notify("error", err.response?.data?.message || "Failed to load practitioner");
    } finally {
      setLoading(false);
    }
  }, [practitionerId, notify]);

  useEffect(() => {
    if (open && practitionerId) load();
  }, [open, practitionerId, load]);

  const uploadToS3 = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", await compressImage(file)); // shrink big photos → avoid 413
    const res = await axios.post(`${BASE_URL}aws/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data?.fileUrl || null;
  }, []);

  const handleSingleUpload = useCallback(
    async (fieldKey, file) => {
      if (!file) return;
      setUploading((u) => ({ ...u, [fieldKey]: true }));
      try {
        const url = await uploadToS3(file);
        if (url) setField(fieldKey, url);
      } catch (err) {
        notify("error", err.response?.data?.message || "Upload failed");
      } finally {
        setUploading((u) => ({ ...u, [fieldKey]: false }));
      }
    },
    [uploadToS3, setField, notify]
  );

  const handleMultiUpload = useCallback(
    async (fieldKey, fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      setUploading((u) => ({ ...u, [fieldKey]: true }));
      try {
        const urls = [];
        for (const f of files) {
          const url = await uploadToS3(f);
          if (url) urls.push(url);
        }
        setForm((f) => ({ ...f, [fieldKey]: [...(f[fieldKey] || []), ...urls] }));
      } catch (err) {
        notify("error", err.response?.data?.message || "Upload failed");
      } finally {
        setUploading((u) => ({ ...u, [fieldKey]: false }));
      }
    },
    [uploadToS3, notify]
  );

  const handleRemoveFile = useCallback((fieldKey, index) => {
    setForm((f) => {
      if (index === undefined) return { ...f, [fieldKey]: "" };
      const arr = Array.isArray(f[fieldKey]) ? f[fieldKey].slice() : [];
      arr.splice(index, 1);
      return { ...f, [fieldKey]: arr };
    });
  }, []);

  const validate = useCallback(() => {
    if (!form.firstName?.trim() || !form.lastName?.trim()) {
      return "First and last name are required.";
    }
    if (form.hourlyRate !== "" && form.hourlyRate != null && Number(form.hourlyRate) < 0) {
      return "Hourly rate cannot be negative.";
    }
    const urlFields = [
      ["Instagram", form.instagram],
      ["Facebook", form.fb],
      ["LinkedIn", form.linkedIn],
      ["Website", form.website],
      ["SimplePractice link", form.simplePracticeBookingUrl],
    ];
    for (const [label, val] of urlFields) {
      if (!isValidUrl(val)) return `${label} must be a valid URL.`;
    }
    return null;
  }, [form]);

  const handleSave = useCallback(async () => {
    const err = validate();
    if (err) {
      notify("error", err);
      return false;
    }
    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone,
        gender: form.gender,
        timeZone: form.timeZone,
        city: form.city,
        country: form.country,
        bio: form.bio,
        specialities: form.specialities,
        languages: form.languages,
        hourlyRate: form.hourlyRate === "" ? null : Number(form.hourlyRate),
        licensed: !!form.licensed,
        instagram: form.instagram,
        fb: form.fb,
        linkedIn: form.linkedIn,
        website: form.website,
        simplePracticeBookingUrl: form.simplePracticeBookingUrl,
        avatar: form.avatar,
        avatarPosition: form.avatarPosition,
      };
      MULTILINE_ARRAY_FIELDS.forEach((f) => (payload[f] = textToArr(form[f])));
      SINGLE_DOC_FIELDS.forEach((k) => (payload[k] = form[k] || ""));
      MULTI_DOC_FIELDS.forEach((k) => (payload[k] = form[k] || []));

      await axiosInstance.put(`practitioners/${practitionerId}`, payload);
      notify("success", "Changes staged — review and publish them");
      return true;
    } catch (err2) {
      notify("error", err2.response?.data?.message || "Failed to save practitioner");
      return false;
    } finally {
      setSaving(false);
    }
  }, [form, validate, notify, practitionerId]);

  return {
    loading,
    saving,
    uploading,
    form,
    setField,
    handleSingleUpload,
    handleMultiUpload,
    handleRemoveFile,
    handleSave,
  };
};
